import type {
	LauncherAnchor,
	LauncherRect,
	LauncherViewport as Viewport,
} from "@weloop/core/widget/launcher-anchor";

import {
	LAUNCHER_GAP_PX,
	sideOfLauncher,
	VIEWPORT_MARGIN_PX,
} from "@weloop/core/widget/launcher-anchor";

import type { FabPosition } from "./types";

// Where the surfaces that belong to the launcher sit on the host page. The
// pure geometry lives in `@weloop/core/widget/launcher-anchor`, so the
// dashboard's previews pin their stand-in surfaces with the same rules; this
// module applies it to real elements.

export {
	computeLauncherAnchor,
	type LauncherAnchor,
	type LauncherRect,
	sideOfLauncher,
} from "@weloop/core/widget/launcher-anchor";

/**
 * Pin a surface beside a launcher that sits flush on a side edge – the
 * vertical tab – top-aligned with it and kept inside the viewport.
 */
export const computeBesideAnchor = (
	side: FabPosition,
	launcher: LauncherRect,
	viewport: Viewport,
	surfaceHeight: number,
): LauncherAnchor => {
	const insetPx =
		(side === "left" ? launcher.right : viewport.width - launcher.left) +
		LAUNCHER_GAP_PX;
	const maxTop = viewport.height - surfaceHeight - VIEWPORT_MARGIN_PX;
	const topPx = Math.max(Math.min(launcher.top, maxTop), VIEWPORT_MARGIN_PX);
	return { insetPx, side, topPx };
};

/**
 * Write an anchor as inline styles, which outrank the stylesheet's corner
 * defaults. Both sides are always written so a previous anchor cannot linger.
 * `data-position` follows the side, so side-keyed CSS (arrows, furl
 * direction) points at the launcher.
 *
 * Without an anchor the inline styles are cleared and `fallbackSide` restored,
 * so the stylesheet's own placement applies again.
 */
export const applyAnchor = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLElement is an external mutable DOM interface
	host: HTMLElement,
	anchor: LauncherAnchor | undefined,
	fallbackSide: FabPosition,
): void => {
	if (anchor === undefined) {
		host.dataset["position"] = fallbackSide;
		// An empty string removes the inline declaration.
		host.style.left = "";
		host.style.right = "";
		host.style.top = "";
		host.style.bottom = "";
		return;
	}
	host.dataset["position"] = anchor.side;
	host.style.left = anchor.side === "left" ? `${anchor.insetPx}px` : "auto";
	host.style.right = anchor.side === "left" ? "auto" : `${anchor.insetPx}px`;
	host.style.top = anchor.topPx === undefined ? "auto" : `${anchor.topPx}px`;
	host.style.bottom =
		anchor.bottomPx === undefined ? "auto" : `${anchor.bottomPx}px`;
};

/**
 * A surface that follows the launcher. Positioning is separate from
 * visibility: neither method ever shows a surface the caller has hidden.
 */
export interface LauncherFollower {
	/**
	 * Where the launcher rests (see `FabHandle.restingRect`). `undefined`
	 * while it is hidden, so the stylesheet's corner placement applies.
	 */
	readonly setLauncher: (launcher: LauncherRect | undefined) => void;
	/**
	 * Hide the surface through `visibility` while the launcher is dragged, so
	 * it does not float apart from it. Touches no other state – a dismissed
	 * surface stays dismissed once the drag ends.
	 */
	readonly setLauncherMoving: (moving: boolean) => void;
}

interface FollowerOptions {
	/** The anchor for a launcher rectangle and the surface's own height. */
	readonly anchorFor: (
		side: FabPosition,
		launcher: LauncherRect,
		viewport: Viewport,
		surfaceHeight: number,
	) => LauncherAnchor;
	readonly fallbackSide: FabPosition;
}

/**
 * Wire a surface host to the launcher. `place` re-pins the surface; the
 * surface calls it right after it becomes visible (in the same task, so the
 * browser never paints the old spot) and when its height changes.
 */
export const createLauncherFollower = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLElement is an external mutable DOM interface
	host: HTMLElement,
	options: FollowerOptions,
): LauncherFollower & { readonly place: () => void } => {
	let launcher: LauncherRect | undefined = undefined;

	const place = (): void => {
		if (launcher === undefined) {
			applyAnchor(host, undefined, options.fallbackSide);
			return;
		}
		// A hidden surface measures 0 tall – it is placed again on show.
		const height = host.offsetHeight;
		if (height === 0) {
			return;
		}
		const viewport = { height: window.innerHeight, width: window.innerWidth };
		applyAnchor(
			host,
			options.anchorFor(
				sideOfLauncher(launcher, viewport),
				launcher,
				viewport,
				height,
			),
			options.fallbackSide,
		);
	};

	return {
		place,
		setLauncher: (next) => {
			launcher = next;
			place();
		},
		setLauncherMoving: (moving) => {
			host.toggleAttribute("data-launcher-moving", moving);
		},
	};
};

/** The launcher as its surfaces see it – `FabHandle` satisfies it. */
export interface MovableLauncher {
	readonly onPlaceChange: (
		listener: (change: "moving" | "settled") => void,
	) => () => void;
	readonly restingRect: () => LauncherRect | undefined;
}

/**
 * Keep surfaces next to the launcher. While it is dragged they are out of
 * sight; once it rests they are re-pinned first and made visible second, so
 * none paints a frame at the spot it left. Positioning only – nothing here
 * shows or hides a surface, so a dismissed one stays dismissed.
 *
 * Returns the unsubscribe.
 */
export const followLauncher = (
	launcher: MovableLauncher,
	// An entry is `undefined` for a surface this page did not mount.
	surfaces: readonly (Partial<LauncherFollower> | undefined)[],
): (() => void) => {
	const place = (): void => {
		const rect = launcher.restingRect();
		for (const surface of surfaces) {
			surface?.setLauncher?.(rect);
		}
	};
	const setMoving = (moving: boolean): void => {
		for (const surface of surfaces) {
			surface?.setLauncherMoving?.(moving);
		}
	};
	// A position restored from the previous page settled before this
	// subscription existed.
	place();
	return launcher.onPlaceChange((change) => {
		if (change === "moving") {
			setMoving(true);
			return;
		}
		place();
		setMoving(false);
	});
};
